import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { UserService } from '../../core/data-services/user.service';
import { User } from '../../core/data-models/user.model';
import { firstValueFrom } from 'rxjs';
import { getLoginPlatformBadge } from '../../shared/utils/login-platform.utils';
import { getMembershipPlanBadge } from '../../shared/utils/membership-plan.utils';

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss'
})
export class UsersComponent implements OnInit {
  users: User[] = [];
  loading = true;
  errorMessage = '';

  // Pagination
  currentPage = 1;
  pageSize = 20;
  totalUsers = 0;
  totalPages = 0;

  // Filters
  searchQuery = '';
  filterEmailVerified: 'all' | 'verified' | 'unverified' = 'all';
  filter2FA: 'all' | 'enabled' | 'disabled' = 'all';
  filterLocked: 'all' | 'locked' | 'unlocked' = 'all';
  filterMembershipPlan: 'all' | 'core' | 'pro' = 'all';
  sortBy: 'createdAt' | 'email' | 'firstName' = 'createdAt';
  sortOrder: 'asc' | 'desc' = 'desc';

  constructor(
    private userService: UserService,
    private router: Router
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadUsers();
  }

  async loadUsers(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      // The hub's list has no lock filter (it always ignored `isLocked`), so `filterLocked` is not sent.
      const page = await firstValueFrom(this.userService.getUsers({
        page: this.currentPage,
        pageSize: this.pageSize,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
        search: this.searchQuery,
        emailVerified: this.filterEmailVerified === 'all' ? undefined : String(this.filterEmailVerified === 'verified'),
        is2faEnabled: this.filter2FA === 'all' ? undefined : String(this.filter2FA === 'enabled'),
        membershipPlan: this.filterMembershipPlan === 'all' ? undefined : this.filterMembershipPlan,
      }));

      this.users = page.items;
      this.totalUsers = page.paging.totalItems;
      this.totalPages = page.paging.totalPages;

    } catch (error: any) {
      console.error('Load users error:', error);
      this.errorMessage = 'Failed to load users';
    } finally {
      this.loading = false;
    }
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadUsers();
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadUsers();
  }

  showClearFilters(): boolean {
    return !!(this.searchQuery || this.filterEmailVerified !== 'all' || this.filter2FA !== 'all' || this.filterLocked !== 'all' || this.filterMembershipPlan !== 'all' || this.sortBy !== 'createdAt' || this.sortOrder !== 'desc');
  }

  resetFilters(): void {
    this.searchQuery = '';
    this.filterEmailVerified = 'all';
    this.filter2FA = 'all';
    this.filterLocked = 'all';
    this.filterMembershipPlan = 'all';
    this.sortBy = 'createdAt';
    this.sortOrder = 'desc';
    this.onFilterChange();
  }

  onSortChange(): void {
    this.loadUsers();
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadUsers();
    }
  }

  viewUser(userId: string): void {
    this.router.navigate(['/users', userId]);
  }

  createUser(): void {
    this.router.navigate(['/users/create']);
  }

  createUserOrganization(): void {
    this.router.navigate(['/users/create-organization']);
  }

  formatDate(dateString: Date | string | null | undefined): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  readonly getLoginPlatformBadge = getLoginPlatformBadge;
  readonly getMembershipPlanBadge = getMembershipPlanBadge;

  get paginationRange(): number[] {
    const range: number[] = [];
    const maxButtons = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxButtons / 2));
    let end = Math.min(this.totalPages, start + maxButtons - 1);

    if (end - start < maxButtons - 1) {
      start = Math.max(1, end - maxButtons + 1);
    }

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    return range;
  }

  // Expose Math for template
  readonly Math = Math;
}
